import { Injectable, TransferState, makeStateKey } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { BehaviorSubject, Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { SupportedLanguage, supportedLanguages } from '../types/lang.type';
import { StorageService } from './storage.service';

@Injectable({
  providedIn: 'root'
})
export class TranslationService {
  private translations = new BehaviorSubject<any>({});
  private currentLang = new BehaviorSubject<SupportedLanguage>('en');
  private loadedTranslation = '';

  constructor(
    private http: HttpClient,
    private storageService: StorageService,
    private state: TransferState
  ) { }

  loadTranslations(lang: SupportedLanguage): Promise<void> {
    this.storageService.setLanguage(lang);
    if (this.loadedTranslation === lang) {
      // console.log('TranslationService loadTranslations already loaded', lang);
      return Promise.resolve();
    }
    this.loadedTranslation = lang;
    // console.log('TranslationService loadTranslations', lang);
    const stateKey = makeStateKey<any>(`i18n-${lang}`);

    // ✅ Use TransferState to avoid HTTP during SSR
    if (this.state.hasKey(stateKey)) {
      const translations = this.state.get(stateKey, {});
      this.translations.next(translations);
      this.currentLang.next(lang);
      // console.log('TranslationService loadTranslations from state', lang);
      return Promise.resolve();
    }

    const isServer = typeof window === 'undefined';

    return new Promise(async (resolve, reject) => {
      if (isServer) {
        try {
          // console.log('TranslationService PRERENDERING STAGE');
          const fs = await import('fs/promises');
          const path = await import('path');

          const filePath = path.join(process.cwd(), 'dist', 'xedoom', 'browser', 'assets', 'i18n', `${lang}.json`);
          const raw = await fs.readFile(filePath, 'utf8');
          const translations = JSON.parse(raw);
          this.translations.next(translations);
          this.currentLang.next(lang);
          this.state.set(stateKey, translations);
          resolve();
        } catch (error) {
          console.error('❌ Error loading translation during prerender:', error);
          reject(error);
        }
      } else {
        // console.log('TranslationService CLIENT');
        this.http.get(`/assets/i18n/${lang}.json`).subscribe({
          next: (translations) => {
            this.translations.next(translations);
            this.currentLang.next(lang);
            // console.log('TranslationService SAVE LANGUAGE', lang);
            this.state.set(stateKey, translations);
            resolve();
          },
          error: (err) => {
            console.error('Error loading translations:', err);
            if (lang !== 'en') {
              this.loadTranslations('en').then(resolve).catch(reject);
            } else {
              reject(err);
            }
          }
        });
      }
    });
  }

  translateBatch(keys: string[], replacements: string[] | null = null): Observable<string[]> {
    return this.translations.pipe(
      map(translations => {
        const resolve = (k: string, i: number): string => {
          const value = k.split('.').reduce((obj, part) => obj?.[part], translations);
          return replacements && replacements[i] && value ? value.replace('%s', replacements[i]) : (value || k);
        };

        return keys.map((k, i) => resolve(k, i));
      })
    );
  }

  translate(key: string, replacement: string | null = null): Observable<string> {
    return this.translations.pipe(
      map(translations => {
        const value = key.split('.').reduce((obj, k) => obj?.[k], translations);
        if (value && replacement) {
          return value.replace('%s', replacement);
        }
        return value || key;
      })
    );
  }

  getCurrentLang(): Observable<SupportedLanguage> {
    return this.currentLang.asObservable();
  }

  getAvailableLanguages(): { code: SupportedLanguage, name: string, short: string, image: string }[] {
    return [
      { code: 'en', name: 'English', short: 'Eng', image: 'assets/icons/lang/us.svg' },
      { code: 'es', name: 'Español', short: 'Esp', image: 'assets/icons/lang/es.svg' },
      { code: 'ru', name: 'Русский', short: 'Рус', image: 'assets/icons/lang/ru.svg' },
      { code: 'uk', name: 'Українська', short: 'Укр', image: 'assets/icons/lang/ua.svg' },
      // { code: 'fr', name: 'Français' },
      // { code: 'de', name: 'Deutsch' }
    ];
  }

  getAllLanguages(): { code: string, name: string, short: string, image: string }[] {
    return [
      { code: 'en', name: 'English', short: 'Eng', image: 'assets/icons/lang/us.svg' },
      { code: 'es', name: 'Español', short: 'Esp', image: 'assets/icons/lang/es.svg' },
      { code: 'ru', name: 'Русский', short: 'Рус', image: 'assets/icons/lang/ru.svg' },
      { code: 'uk', name: 'Українська', short: 'Укр', image: 'assets/icons/lang/ua.svg' },
      { code: 'fr', name: 'Français', short: 'Fra', image: 'assets/icons/lang/fr.svg' },
      { code: 'de', name: 'Deutsch', short: 'Deu', image: 'assets/icons/lang/de.svg' },
      { code: 'zh', name: '中文', short: '中文', image: 'assets/icons/lang/cn.svg' },
      { code: 'ja', name: '日本語', short: '日', image: 'assets/icons/lang/jp.svg' },
      { code: 'ko', name: '한국어', short: '한', image: 'assets/icons/lang/kr.svg' },
      { code: 'pt', name: 'Português', short: 'Por', image: 'assets/icons/lang/pt.svg' },
      { code: 'it', name: 'Italiano', short: 'Ita', image: 'assets/icons/lang/it.svg' },
      { code: 'pl', name: 'Polski', short: 'Pol', image: 'assets/icons/lang/pl.svg' },
      { code: 'nl', name: 'Nederlands', short: 'Ned', image: 'assets/icons/lang/nl.svg' },
      { code: 'tr', name: 'Türkçe', short: 'Tr', image: 'assets/icons/lang/tr.svg' },
      { code: 'ar', name: 'العربية', short: 'عر', image: 'assets/icons/lang/sa.svg' },
      { code: 'hi', name: 'हिन्दी', short: 'हि', image: 'assets/icons/lang/in.svg' },
      { code: 'bn', name: 'বাংলা', short: 'বাং', image: 'assets/icons/lang/bd.svg' },
      { code: 'vi', name: 'Tiếng Việt', short: 'Vie', image: 'assets/icons/lang/vn.svg' },
      { code: 'th', name: 'ไทย', short: 'ไท', image: 'assets/icons/lang/th.svg' },
      { code: 'fa', name: 'فارسی', short: 'فا', image: 'assets/icons/lang/ir.svg' },
      { code: 'sv', name: 'Svenska', short: 'Sve', image: 'assets/icons/lang/se.svg' },
      { code: 'fi', name: 'Suomi', short: 'Fio', image: 'assets/icons/lang/fi.svg' },
      { code: 'no', name: 'Norsk', short: 'Nor', image: 'assets/icons/lang/no.svg' },
      { code: 'da', name: 'Dansk', short: 'Dan', image: 'assets/icons/lang/dk.svg' },
      { code: 'cs', name: 'Čeština', short: 'Češ', image: 'assets/icons/lang/cz.svg' },
      { code: 'el', name: 'Ελληνικά', short: 'Ελλ', image: 'assets/icons/lang/gr.svg' },
      { code: 'he', name: 'עברית', short: 'עב', image: 'assets/icons/lang/il.svg' },
      { code: 'ro', name: 'Română', short: 'Rom', image: 'assets/icons/lang/ro.svg' },
      { code: 'hu', name: 'Magyar', short: 'Mag', image: 'assets/icons/lang/hu.svg' },
      { code: 'sr', name: 'Српски', short: 'Срп', image: 'assets/icons/lang/rs.svg' },
      { code: 'hr', name: 'Hrvatski', short: 'Hrv', image: 'assets/icons/lang/hr.svg' },
      { code: 'sk', name: 'Slovenčina', short: 'Slo', image: 'assets/icons/lang/sk.svg' },
      { code: 'sl', name: 'Slovenščina', short: 'Slv', image: 'assets/icons/lang/si.svg' },
      { code: 'id', name: 'Bahasa Indonesia', short: 'Ind', image: 'assets/icons/lang/id.svg' },
      { code: 'ms', name: 'Bahasa Melayu', short: 'Mly', image: 'assets/icons/lang/my.svg' },
      { code: 'ta', name: 'தமிழ்', short: 'தம', image: 'assets/icons/lang/in.svg' },
      { code: 'te', name: 'తెలుగు', short: 'తెల', image: 'assets/icons/lang/in.svg' },
      { code: 'kn', name: 'ಕನ್ನಡ', short: 'ಕ', image: 'assets/icons/lang/in.svg' },
      { code: 'ml', name: 'മലയാളം', short: 'മ', image: 'assets/icons/lang/in.svg' },
      { code: 'mr', name: 'मराठी', short: 'मरा', image: 'assets/icons/lang/in.svg' },
      { code: 'gu', name: 'ગુજરાતી', short: 'ગુ', image: 'assets/icons/lang/in.svg' },
      { code: 'pa', name: 'ਪੰਜਾਬੀ', short: 'ਪੰ', image: 'assets/icons/lang/in.svg' },
      { code: 'am', name: 'አማርኛ', short: 'አማ', image: 'assets/icons/lang/et.svg' },
      { code: 'sw', name: 'Kiswahili', short: 'Swa', image: 'assets/icons/lang/tz.svg' },
      { code: 'zu', name: 'IsiZulu', short: 'Zul', image: 'assets/icons/lang/za.svg' },
      { code: 'xh', name: 'IsiXhosa', short: 'Xho', image: 'assets/icons/lang/za.svg' },
      { code: 'af', name: 'Afrikaans', short: 'Afr', image: 'assets/icons/lang/za.svg' },
      { code: 'ha', name: 'Hausa', short: 'Hau', image: 'assets/icons/lang/ng.svg' },
      { code: 'ig', name: 'Igbo', short: 'Igb', image: 'assets/icons/lang/ng.svg' },
      { code: 'yo', name: 'Yorùbá', short: 'Yor', image: 'assets/icons/lang/ng.svg' },
      { code: 'az', name: 'Azərbaycanca', short: 'Aze', image: 'assets/icons/lang/az.svg' },
      { code: 'ka', name: 'ქართული', short: 'ქარ', image: 'assets/icons/lang/ge.svg' },
      { code: 'kk', name: 'Қазақ тілі', short: 'Қаз', image: 'assets/icons/lang/kz.svg' },
      { code: 'uz', name: 'O‘zbekcha', short: 'Uzb', image: 'assets/icons/lang/uz.svg' },
      { code: 'mn', name: 'Монгол', short: 'Мон', image: 'assets/icons/lang/mn.svg' },
      { code: 'my', name: 'ဗမာစာ', short: 'ဗ', image: 'assets/icons/lang/mm.svg' },
      { code: 'km', name: 'ភាសាខ្មែរ', short: 'ខ្មែរ', image: 'assets/icons/lang/kh.svg' },
      { code: 'lo', name: 'ລາວ', short: 'ລ', image: 'assets/icons/lang/la.svg' },
      { code: 'si', name: 'සිංහල', short: 'සිං', image: 'assets/icons/lang/lk.svg' },
      { code: 'ne', name: 'नेपाली', short: 'ने', image: 'assets/icons/lang/np.svg' }
    ];
  }

  getLangImage(lang: string): string {
    return this.getAllLanguages().find(l => l.code === lang)?.image || '';
  }
}

export function isSupportedLanguage(lang: string): lang is SupportedLanguage {
  return supportedLanguages.includes(lang as SupportedLanguage);
}
